import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, User, Mail, Calendar, MapPin, Briefcase, Shield, Ban, Clock,
  CreditCard, AlertTriangle, CheckCircle, XCircle, Key, Eye, Plus, Trash2,
  Star, Phone, Image, FileText, Loader2, Globe, Hammer, Wallet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BACKEND_URL } from '../config/api';

export default function AdminUserProfile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ note_type: 'other', content: '' });
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    checkAuth();
  }, [userId]);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (res.data.user_type !== 'admin') { navigate('/login'); return; }
      fetchUserData();
      fetchPlans();
    } catch { navigate('/login'); }
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/subscription-plans`, { withCredentials: true });
      setPlans((res.data || []).filter(p => p.plan_type !== 'tradesperson'));
    } catch { /* non-fatal */ }
  };

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/user-profile/${userId}`, { withCredentials: true });
      setUserData(res.data);
    } catch {
      toast.error('Failed to load user data');
      navigate('/admin/users');
    } finally { setLoading(false); }
  };

  const handleAddNote = async () => {
    if (!noteForm.content.trim()) { toast.error('Note content is required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/notes`, noteForm, { withCredentials: true });
      toast.success('Note added');
      setShowNoteModal(false);
      setNoteForm({ note_type: 'other', content: '' });
      fetchUserData();
    } catch { toast.error('Failed to add note'); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/notes/${noteId}`, { withCredentials: true });
      toast.success('Note deleted');
      fetchUserData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete note'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const user = userData?.user;
  const profile = userData?.profile;
  const subscription = userData?.subscription;
  const credits = userData?.credits;
  const accountStatus = userData?.account_status || 'active';
  const uploads = userData?.uploads || [];
  const profilePhotos = uploads.filter(u => u.category === 'profile-photo');
  const portfolioImages = uploads.filter(u => u.category === 'portfolio');
  const profilePhotoUrl = profile?.profile_image || (profilePhotos.length > 0 ? `${BACKEND_URL}/api/uploads/serve/${profilePhotos[0].file_id}` : null);

  const STATUS_BADGE = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    suspended: 'bg-amber-100 text-amber-700 border-amber-200',
    banned: 'bg-red-100 text-red-700 border-red-200',
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'notes', label: `Notes (${userData?.admin_notes?.length || 0})` },
    { id: 'history', label: 'History' },
  ];
  if (user?.user_type === 'employer') tabs.splice(2, 0, { id: 'subscription', label: 'Billing' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-user-profile">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">User Profile</h1>
              <p className="text-xs text-slate-500">Admin read-only view</p>
            </div>
          </div>
          <button onClick={() => navigate('/admin/users')} className="text-sm text-slate-500 hover:text-slate-700">
            Back to User Management
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* User Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="user-header-card">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            {profilePhotoUrl ? (
              <img src={profilePhotoUrl} alt={user?.name} className="w-20 h-20 rounded-full object-cover border-2 border-slate-200" data-testid="user-avatar" />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shrink-0" data-testid="user-avatar-placeholder">
                <User className="w-10 h-10 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-slate-900" data-testid="user-name">{user?.name}</h2>
              <p className="text-slate-500">{user?.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE[accountStatus] || STATUS_BADGE.active}`}>
                  {accountStatus.toUpperCase()}
                </span>
                <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium capitalize">
                  {user?.user_type}
                </span>
                {user?.email_verified && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full text-xs">
                    <CheckCircle className="w-3 h-3" /> Verified
                  </span>
                )}
                {!userData?.is_active && (
                  <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded-full text-xs">DELETED</span>
                )}
                {user?.grandfather_status === 'active' && (
                  <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full text-xs">{user.grandfather_label || 'Founding Member'}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                activeTab === tab.id ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === 'overview' && (
              <>
                {/* Core Info */}
                <Section title="Account Information" icon={<User className="w-4 h-4" />}>
                  <InfoGrid>
                    <InfoItem label="Full Name" value={user?.name} />
                    <InfoItem label="Email" value={user?.email} />
                    <InfoItem label="Phone" value={user?.phone} />
                    <InfoItem label="Account Type" value={user?.user_type} capitalize />
                    <InfoItem label="Date Joined" value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-GB') : null} />
                    <InfoItem label="Last Active" value={user?.last_seen ? new Date(user.last_seen).toLocaleString('en-GB') : 'Never'} />
                    <InfoItem label="Email Verified" value={user?.email_verified ? 'Yes' : 'No'} color={user?.email_verified ? 'text-emerald-600' : 'text-red-500'} />
                    <InfoItem label="Postcode" value={user?.postcode} />
                    <InfoItem label="Referral Code" value={user?.referral_code} />
                    <InfoItem label="Approval Status" value={user?.approval_status} capitalize />
                  </InfoGrid>
                </Section>

                {/* Bio */}
                {(user?.bio || profile?.bio) && (
                  <Section title="Bio / About" icon={<FileText className="w-4 h-4" />}>
                    <p className="text-slate-700 text-sm whitespace-pre-wrap" data-testid="user-bio">{profile?.bio || user?.bio}</p>
                  </Section>
                )}

                {/* Tradesperson Profile */}
                {user?.user_type === 'tradesperson' && profile && (
                  <Section title="Trade Profile" icon={<Hammer className="w-4 h-4" />}>
                    <InfoGrid>
                      <InfoItem label="Trade" value={profile.trade || user?.trade} />
                      <InfoItem label="Daily Rate" value={profile.daily_rate ? `£${profile.daily_rate}` : null} />
                      <InfoItem label="Experience" value={profile.experience_years ? `${profile.experience_years} years` : null} />
                      <InfoItem label="Insurance" value={profile.insurance ? 'Yes' : 'No'} />
                      <InfoItem label="Transport" value={profile.transport} capitalize />
                      <InfoItem label="Profile Status" value={profile.profile_status} capitalize />
                      <InfoItem label="Visibility" value={profile.profile_visibility} capitalize />
                      <InfoItem label="Profile Complete" value={profile.profile_complete ? 'Yes' : 'No'} />
                    </InfoGrid>
                    {profile.qualifications?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-500 mb-1">Qualifications</p>
                        <div className="flex flex-wrap gap-1">{profile.qualifications.map((q, i) => <Tag key={i} text={q} />)}</div>
                      </div>
                    )}
                    {(profile.work_areas?.length > 0 || user?.work_areas?.length > 0) && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-500 mb-1">Work Areas</p>
                        <div className="flex flex-wrap gap-1">{(profile.work_areas || user?.work_areas || []).map((a, i) => <Tag key={i} text={a} />)}</div>
                      </div>
                    )}
                  </Section>
                )}

                {/* Availability */}
                {user?.user_type === 'tradesperson' && (
                  <Section title="Availability" icon={<Clock className="w-4 h-4" />}>
                    <InfoGrid>
                      <InfoItem label="Mode" value={profile?.availability_mode || user?.availability_mode} capitalize />
                      <InfoItem label="Available From" value={profile?.available_from_date ? new Date(profile.available_from_date).toLocaleDateString('en-GB') : null} />
                      <InfoItem label="Evening Work" value={profile?.open_to_evening_work ? 'Yes' : 'No'} />
                      <InfoItem label="Weekend Work" value={profile?.open_to_weekend_work ? 'Yes' : 'No'} />
                      <InfoItem label="Urgent Alerts (While Busy)" value={profile?.receive_urgent_alerts_while_busy ? 'Yes' : 'No'} />
                    </InfoGrid>
                    {profile?.availability_notes && <p className="text-sm text-slate-600 mt-2">{profile.availability_notes}</p>}
                  </Section>
                )}

                {/* Employer Credits */}
                {user?.user_type === 'employer' && credits && (
                  <Section title="Credits" icon={<Wallet className="w-4 h-4" />}>
                    <InfoGrid>
                      <InfoItem label="Balance" value={`${credits.balance || 0} credits`} />
                      <InfoItem label="Is Founding" value={credits.is_founding ? 'Yes' : 'No'} />
                      {credits.bonus_active && <InfoItem label="Bonus" value={`${credits.bonus_percent || 0}%`} />}
                    </InfoGrid>
                  </Section>
                )}

                {/* Credentials */}
                {userData?.credentials?.length > 0 && (
                  <Section title="Credentials" icon={<Shield className="w-4 h-4" />}>
                    <div className="space-y-2">
                      {userData.credentials.map((cred, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{cred.credential_type}</p>
                            <p className="text-xs text-slate-500">{cred.credential_number || 'No number'}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            cred.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            cred.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>{cred.status || 'pending'}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Portfolio / Gallery */}
                {portfolioImages.length > 0 && (
                  <Section title="Portfolio / Work Images" icon={<Image className="w-4 h-4" />}>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="portfolio-gallery">
                      {portfolioImages.map(img => (
                        <img key={img.file_id} src={`${BACKEND_URL}/api/uploads/serve/${img.file_id}`}
                          alt="Portfolio" className="w-full h-24 object-cover rounded-lg border border-slate-200" />
                      ))}
                    </div>
                  </Section>
                )}

                {/* Reviews Summary */}
                {userData?.reviews?.length > 0 && (
                  <Section title={`Reviews (${userData.reviews.length})`} icon={<Star className="w-4 h-4" />}>
                    <div className="space-y-2">
                      {userData.reviews.slice(0, 5).map((rev, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex items-center gap-0.5">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-3 h-3 ${s <= (rev.overall_rating || rev.quality || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                              ))}
                            </div>
                            <span className="text-xs text-slate-500">{rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-GB') : ''}</span>
                          </div>
                          {rev.comment && <p className="text-sm text-slate-700">{rev.comment}</p>}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </>
            )}

            {activeTab === 'notes' && (
              <Section title="Admin Notes" icon={<FileText className="w-4 h-4" />}
                action={<button onClick={() => setShowNoteModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600" data-testid="add-note-btn"><Plus className="w-3.5 h-3.5" /> Add Note</button>}>
                {userData?.admin_notes?.length > 0 ? (
                  <div className="space-y-2">
                    {userData.admin_notes.map(note => (
                      <div key={note.note_id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              note.note_type === 'suspicious_activity' ? 'bg-red-100 text-red-700' :
                              note.note_type === 'payment_dispute' ? 'bg-orange-100 text-orange-700' :
                              note.note_type === 'customer_complaint' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>{note.note_type.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-slate-400">by {note.admin_name} &middot; {new Date(note.created_at).toLocaleString('en-GB')}</span>
                          </div>
                          <button onClick={() => handleDeleteNote(note.note_id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                        <p className="text-sm text-slate-700">{note.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm text-center py-8">No admin notes yet</p>
                )}
              </Section>
            )}

            {activeTab === 'subscription' && (
              <>
                <Section title="Subscription & Billing" icon={<CreditCard className="w-4 h-4" />}>
                  {subscription ? (
                    <div className="space-y-4">
                      <InfoGrid>
                        <InfoItem label="Status" value={subscription.active ? 'Active' : 'Inactive'} color={subscription.active ? 'text-emerald-600' : 'text-red-500'} />
                        <InfoItem label="Plan" value={subscription.plan_name || subscription.plan_id} />
                        {subscription.end_date && <InfoItem label="Renews / Expires" value={new Date(subscription.end_date).toLocaleString('en-GB')} />}
                        {subscription.source && <InfoItem label="Source" value={subscription.source} capitalize />}
                        {subscription.free_access && <InfoItem label="Free Access" value="Yes" color="text-emerald-600" />}
                        {subscription.free_access_expires && <InfoItem label="Free Access Expires" value={new Date(subscription.free_access_expires).toLocaleDateString('en-GB')} />}
                      </InfoGrid>
                      {userData?.payment_history?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-slate-700 mb-2">Payment History</p>
                          <div className="space-y-1">
                            {userData.payment_history.slice(0, 10).map((p, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded border border-slate-100 text-sm">
                                <span className="text-slate-700">£{p.amount || 0}</span>
                                <span className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString('en-GB')}</span>
                                <span className={`px-2 py-0.5 rounded text-xs ${p.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' : p.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm text-center py-4">No subscription on file. Use Manual Grant below to create one.</p>
                  )}
                </Section>

                <ManualSubscriptionGrantPanel
                  userId={userId}
                  plans={plans}
                  currentSub={subscription}
                  onUpdated={fetchUserData}
                />

                <ManualCreditGrantPanel
                  userId={userId}
                  currentBalance={credits?.balance || credits?.credits || 0}
                  onUpdated={fetchUserData}
                />
              </>
            )}

            {activeTab === 'history' && (
              <>
                <Section title="Login History" icon={<Clock className="w-4 h-4" />}>
                  {userData?.login_history?.length > 0 ? (
                    <div className="space-y-1">
                      {userData.login_history.map((login, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded border border-slate-100 text-sm">
                          <span className="text-slate-700">{new Date(login.login_at).toLocaleString('en-GB')}</span>
                          <span className="text-xs text-slate-400">IP: {login.ip_address}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-slate-400 text-sm text-center py-6">No login history</p>}
                </Section>
                <Section title="Job / Booking History" icon={<Briefcase className="w-4 h-4" />}>
                  {userData?.job_history?.length > 0 ? (
                    <div className="space-y-1">
                      {userData.job_history.map((job, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded border border-slate-100 text-sm">
                          <span className="text-slate-700">{job.job_title || 'Booking'}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${job.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : job.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{job.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-slate-400 text-sm text-center py-6">No job history</p>}
                </Section>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Quick Stats</h3>
              <div className="space-y-2.5">
                <StatRow label="Admin Notes" value={userData?.admin_notes?.length || 0} />
                <StatRow label="Reports Against" value={userData?.reports_against?.length || 0} />
                <StatRow label="Jobs / Bookings" value={userData?.job_history?.length || 0} />
                <StatRow label="Reviews" value={userData?.reviews?.length || 0} />
                <StatRow label="Credentials" value={userData?.credentials?.length || 0} />
                <StatRow label="Uploads" value={uploads.length} />
              </div>
            </div>

            {(accountStatus !== 'active' || !userData?.is_active) && (
              <div className="bg-red-50 rounded-xl border border-red-200 p-5">
                <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Account Issues</h3>
                {accountStatus === 'suspended' && (
                  <p className="text-sm text-red-600">Suspended{user?.suspend_reason ? `: ${user.suspend_reason}` : ''}</p>
                )}
                {accountStatus === 'banned' && (
                  <p className="text-sm text-red-600">Banned{user?.ban_reason ? `: ${user.ban_reason}` : ''}</p>
                )}
                {!userData?.is_active && <p className="text-sm text-red-600">Account soft-deleted</p>}
              </div>
            )}

            {userData?.reports_against?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Reports Against</h3>
                <div className="space-y-2">
                  {userData.reports_against.slice(0, 3).map((r, idx) => (
                    <div key={idx} className="p-2.5 bg-slate-50 rounded border border-slate-100 text-xs">
                      <span className="font-medium text-slate-700">{r.report_type}</span>
                      <p className="text-slate-500 mt-0.5">{r.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Admin Note</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
              <select value={noteForm.note_type} onChange={e => setNoteForm({...noteForm, note_type: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="verification_pending">Verification Pending</option>
                <option value="payment_dispute">Payment Dispute</option>
                <option value="suspicious_activity">Suspicious Activity</option>
                <option value="customer_complaint">Customer Complaint</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
              <textarea value={noteForm.content} onChange={e => setNoteForm({...noteForm, content: e.target.value})}
                rows={4} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Enter note..." />
            </div>
            <button onClick={handleAddNote} className="w-full bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 text-sm font-medium">Add Note</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon, children, action }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">{icon}{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoGrid({ children }) {
  return <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>;
}

function InfoItem({ label, value, capitalize, color }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-sm font-medium ${color || 'text-slate-900'} ${capitalize ? 'capitalize' : ''}`}>{value || <span className="text-slate-300">N/A</span>}</p>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

function Tag({ text }) {
  return <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded border border-slate-200">{text}</span>;
}


function ManualSubscriptionGrantPanel({ userId, plans, currentSub, onUpdated }) {
  const [action, setAction] = useState('activate');
  const [planId, setPlanId] = useState('');
  const [durationDays, setDurationDays] = useState(30);
  const [addCredits, setAddCredits] = useState(true);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!planId && plans?.length) setPlanId(plans[0].plan_id);
  }, [plans, planId]);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Reason / note is required for audit log');
      return;
    }
    if (action === 'activate' && !planId) {
      toast.error('Pick a plan');
      return;
    }
    setSubmitting(true);
    try {
      const body = { action, reason: reason.trim() };
      if (action === 'activate') {
        body.plan_id = planId;
        body.duration_days = Number(durationDays) || 30;
        body.add_credits = !!addCredits;
      } else if (action === 'reactivate') {
        body.duration_days = Number(durationDays) || 30;
      } else if (action === 'grant_free_access') {
        body.free_access_days = Number(durationDays) || 30;
      }
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/subscription/action`,
        body,
        { withCredentials: true },
      );
      toast.success(res.data?.message || 'Subscription updated');
      setReason('');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section title="Manual Subscription Grant (Admin)" icon={<Shield className="w-4 h-4" />}>
      <div className="space-y-3" data-testid="manual-sub-grant-panel">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
          Use to test or comp a user without a Stripe payment. Every action is written to the admin audit log.
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Action</label>
          <select value={action} onChange={(e) => setAction(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="manual-sub-action-select">
            <option value="activate">Activate / Replace plan</option>
            <option value="reactivate">Reactivate existing</option>
            <option value="cancel">Cancel</option>
            <option value="grant_free_access">Grant free access</option>
            <option value="remove_free_access">Remove free access</option>
          </select>
        </div>

        {action === 'activate' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Plan</label>
              <select value={planId} onChange={(e) => setPlanId(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="manual-sub-plan-select">
                {(plans || []).map(p => (
                  <option key={p.plan_id} value={p.plan_id}>
                    {p.name} (£{p.price}/mo · {p.credits || p.chat_allowance || 0} credits)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Duration (days)</label>
              <input type="number" min="1" max="3650" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="manual-sub-duration" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
              <input type="checkbox" checked={addCredits} onChange={(e) => setAddCredits(e.target.checked)} data-testid="manual-sub-add-credits" />
              Also grant the plan's monthly credits
            </label>
          </div>
        )}

        {(action === 'reactivate' || action === 'grant_free_access') && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {action === 'grant_free_access' ? 'Free access days' : 'Extend by (days)'}
            </label>
            <input type="number" min="1" max="3650" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="manual-sub-duration" />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Reason / audit note (required)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Refund replacement, internal QA, founding offer" data-testid="manual-sub-reason" />
        </div>

        <button onClick={submit} disabled={submitting || !reason.trim()} className="px-5 py-2.5 rounded-lg font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed" data-testid="manual-sub-submit-btn">
          {submitting ? 'Applying…' : 'Apply'}
        </button>

        {currentSub && (
          <div className="text-xs text-slate-500">
            Current: {currentSub.plan_name || currentSub.plan_id} — {currentSub.active ? 'active' : 'inactive'}
            {currentSub.end_date ? ` · ends ${new Date(currentSub.end_date).toLocaleDateString('en-GB')}` : ''}
          </div>
        )}
      </div>
    </Section>
  );
}


function ManualCreditGrantPanel({ userId, currentBalance, onUpdated }) {
  const [action, setAction] = useState('add');
  const [amount, setAmount] = useState(10);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Reason / note is required for audit log');
      return;
    }
    const amt = Number(amount);
    if (!Number.isInteger(amt) || amt <= 0) {
      toast.error('Amount must be a whole positive number');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/credits`,
        { action, amount: amt, reason: reason.trim() },
        { withCredentials: true },
      );
      toast.success(`${res.data?.message}. New balance: ${res.data?.balance_after}`);
      setReason('');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section title="Manual Credit Grant (Admin)" icon={<Wallet className="w-4 h-4" />}>
      <div className="space-y-3" data-testid="manual-credit-grant-panel">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
          Add or remove employer credits without Stripe. Adjustments clamp at 0 (cannot go negative). Every change is logged.
        </div>
        <div className="text-sm text-slate-700">
          Current balance: <span className="font-semibold" data-testid="manual-credit-current-balance">{currentBalance ?? 0}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="manual-credit-action-select">
              <option value="add">Add credits</option>
              <option value="remove">Remove credits</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Amount</label>
            <input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="manual-credit-amount" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Reason / audit note (required)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Refund for double-charge, comped credits, dispute resolution" data-testid="manual-credit-reason" />
        </div>
        <button onClick={submit} disabled={submitting || !reason.trim()} className="px-5 py-2.5 rounded-lg font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed" data-testid="manual-credit-submit-btn">
          {submitting ? 'Applying…' : (action === 'add' ? 'Add credits' : 'Remove credits')}
        </button>
      </div>
    </Section>
  );
}
